/**
 * VariantNotificationOverridesDrawer — per-variant Slack notification
 * override editor hosted inside the App Integrations tab (#1184,
 * Slice 1 of meta #1183).
 *
 * Carries forward the Notifications section that previously lived on
 * the standalone variant Automation page. The pattern is symmetric to
 * the trigger-rules drawer: variant row click in the Slack card →
 * drawer slides in → list of existing overrides + inline add/edit
 * form. No nested modal.
 *
 * **Data flow.** Reads the resolved view from `variantsAPI.getNotificationOverridesResolved`
 * (the same server-side resolver the runtime dispatcher consults — keeps
 * the customer-facing hint from drifting from runtime behaviour). The
 * Slack channel picker reuses the App's channel list via
 * `applicationsAPI.getIntegrations(appId)` so the option set matches
 * the App Integrations tab's own picker.
 *
 * **Save semantics.** The endpoint accepts the full override list and
 * replaces the persisted set. The drawer batches in-memory edits and
 * calls `updateNotificationOverrides` once per save / delete; mirrors
 * the pre-#1184 page-level behavior.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Label,
  SearchableSelect,
} from '@/components/ui'
import { useAppContext } from '@/hooks/useAppContext'
import { useConfirm } from '@/hooks/useConfirm'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import {
  applicationsAPI,
  variantsAPI,
  type AppIntegrationsSlackChannel,
  type VariantNotificationOverridesResolved,
} from '@/lib/api'
import {
  APP_SLACK_EVENT_SUBSCRIPTIONS,
  type AppSlackEventSubscription,
} from '@/types'

const NOTIFICATION_EVENT_LABELS: Record<AppSlackEventSubscription, string> = {
  'run.failed': 'On failure',
  'run.completed': 'On completion',
  'run.errored': 'On error',
  'run.cancelled': 'On cancellation',
}

interface SlackChannelLookup {
  byId: Map<string, string>
  options: Array<{ value: string; label: string }>
  connected: boolean
}

function buildChannelLookup(
  channels: AppIntegrationsSlackChannel[] | undefined,
): SlackChannelLookup {
  const byId = new Map<string, string>()
  const options: Array<{ value: string; label: string }> = []
  if (!channels) return { byId, options, connected: false }
  const sorted = [...channels].sort((a, b) => a.name.localeCompare(b.name))
  for (const c of sorted) {
    const label = c.isPrivate ? `🔒 ${c.name}` : `# ${c.name}`
    byId.set(c.id, label)
    options.push({ value: c.id, label })
  }
  return { byId, options, connected: true }
}

function ChannelPill({
  channelId,
  lookup,
}: {
  channelId: string
  lookup: SlackChannelLookup
}) {
  const label = lookup.byId.get(channelId)
  return (
    <code
      className={
        label
          ? 'rounded bg-muted px-1.5 py-0.5 font-mono text-xs'
          : 'rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground'
      }
    >
      {label ?? channelId}
    </code>
  )
}

function InlineEventList({
  events,
}: {
  events: AppSlackEventSubscription[]
}) {
  return (
    <>
      {events.map((ev, i) => (
        <span key={ev}>
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
            {ev}
          </code>
          {i < events.length - 2 && ', '}
          {i === events.length - 2 && ' and '}
        </span>
      ))}
    </>
  )
}

export interface VariantNotificationOverridesDrawerProps {
  appId: string
  variantId: string
  onClose: () => void
}

export function VariantNotificationOverridesDrawer({
  appId,
  variantId,
  onClose,
}: VariantNotificationOverridesDrawerProps) {
  const { allVariants } = useAppContext()
  const confirm = useConfirm()
  const toast = useToast()
  const wp = useWorkspacePath()

  const variant = useMemo(
    () => allVariants.find((v) => v.id === variantId) ?? null,
    [allVariants, variantId],
  )

  // Cross-app guard. See `VariantTriggerRulesDrawer` for the full
  // rationale — same hazard here: a crafted `?variantConfig=<vid>`
  // URL could point at a variant belonging to a different app in the
  // workspace; without this gate the drawer would mutate overrides
  // under the wrong app context. Close the drawer when the IDs
  // diverge.
  useEffect(() => {
    if (variant && variant.applicationId !== appId) {
      onClose()
    }
  }, [variant, appId, onClose])

  const [resolved, setResolved] =
    useState<VariantNotificationOverridesResolved | null>(null)
  const [channels, setChannels] = useState<AppIntegrationsSlackChannel[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editorState, setEditorState] = useState<
    | { mode: 'add' }
    | {
        mode: 'edit'
        existing: { event: AppSlackEventSubscription; channelId: string }
      }
    | null
  >(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, integrationsResp] = await Promise.all([
        variantsAPI.getNotificationOverridesResolved(variantId),
        applicationsAPI.getIntegrations(appId).catch(() => null),
      ])
      setResolved(r)
      setChannels(
        integrationsResp && integrationsResp.slack.connected
          ? integrationsResp.slack.channels
          : null,
      )
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to load notification overrides',
      )
    } finally {
      setLoading(false)
    }
  }, [appId, variantId])

  useEffect(() => {
    void load()
  }, [load])

  const channelLookup = useMemo(
    () => buildChannelLookup(channels ?? undefined),
    [channels],
  )

  async function persist(
    nextOverrides: Array<{
      event: AppSlackEventSubscription
      channelId: string
    }>,
  ) {
    try {
      const updated = await variantsAPI.updateNotificationOverrides(
        variantId,
        nextOverrides,
      )
      setResolved(updated)
    } catch (err) {
      toast({
        title: 'Failed to save notification overrides',
        description: toUserMessage(
          err,
          "Couldn't save the notification overrides. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    }
  }

  async function handleDelete(event: AppSlackEventSubscription) {
    const ok = await confirm({
      title: 'Remove notification override?',
      description: `The variant will go back to the app default for ${NOTIFICATION_EVENT_LABELS[event]}.`,
      confirmLabel: 'Remove override',
      destructive: true,
    })
    if (!ok) return
    const next = (resolved?.overrides ?? []).filter((o) => o.event !== event)
    await persist(next)
  }

  function handleEditorSaved(saved: {
    event: AppSlackEventSubscription
    channelId: string
  }) {
    setEditorState(null)
    const current = resolved?.overrides ?? []
    const filtered = current.filter((o) => o.event !== saved.event)
    void persist([...filtered, saved])
  }

  const variantName = variant?.name ?? 'Variant'
  const ariaLabel = `Slack notification overrides for ${variantName}`
  const overrides = resolved?.overrides ?? []
  const existingOverrideEvents = new Set(overrides.map((o) => o.event))
  const appDefault = resolved?.appDefault ?? null

  return (
    <Drawer open onOpenChange={(open) => !open && onClose()}>
      <DrawerContent ariaLabel={ariaLabel} data-testid="variant-notifications-drawer">
        <DrawerHeader
          eyebrow="Notification overrides"
          title={variantName}
          subtitle="Per-variant Slack channel overrides on top of the app default"
        />
        <DrawerBody>
          {loading && !resolved ? (
            <div className="space-y-2" aria-label="Loading notification overrides">
              {[1, 2].map((i) => (
                <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </div>
          ) : !resolved ? (
            <div
              className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground"
              data-testid="drawer-notifications-not-found"
            >
              Couldn't load notifications for this variant — it may have been
              deleted. Close the drawer and pick another variant.
            </div>
          ) : !resolved.appHasSlackBinding ? (
            <div
              className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground"
              data-testid="drawer-notifications-no-app-binding"
            >
              <strong className="block text-foreground mb-1">
                No Slack default on the app yet.
              </strong>
              Pick a default channel on the Slack card above first; then
              come back here to add per-event overrides for this variant.
              {' '}
              <Link
                to={wp(`/applications?app=${appId}&tab=integrations`)}
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Jump to the Slack card
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Inheritance hint */}
              <div
                className="rounded-md border border-border bg-muted/40 p-3 text-sm"
                data-testid="drawer-notifications-inheritance-hint"
              >
                {appDefault && appDefault.events.length > 0 ? (
                  <span>
                    App default:{' '}
                    <ChannelPill channelId={appDefault.channelId} lookup={channelLookup} />{' '}
                    for{' '}
                    <InlineEventList events={appDefault.events} />.
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    The app has a Slack default channel but no events selected,
                    so nothing fires by default. Add overrides below to send
                    specific events from this variant.
                  </span>
                )}
              </div>

              {/* Overrides list */}
              {overrides.length === 0 && !editorState && (
                <div
                  className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
                  data-testid="drawer-notifications-empty"
                >
                  No overrides yet — this variant uses the app default for every event.
                </div>
              )}

              {overrides.length > 0 && (
                <ul
                  className="space-y-2"
                  data-testid="drawer-notifications-list"
                >
                  {overrides.map((o) => (
                    <li
                      key={o.event}
                      className="rounded-md border border-border bg-background p-3"
                      data-testid="drawer-notification-row"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                          Override
                        </span>
                        <div className="ml-auto flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() =>
                              setEditorState({ mode: 'edit', existing: o })
                            }
                            title="Edit"
                            aria-label="Edit notification override"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => void handleDelete(o.event)}
                            title="Delete"
                            aria-label="Remove notification override"
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm">
                        On{' '}
                        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                          {o.event}
                        </code>{' '}
                        → ping{' '}
                        <ChannelPill channelId={o.channelId} lookup={channelLookup} />{' '}
                        instead of the app default
                      </p>
                    </li>
                  ))}
                </ul>
              )}

              {editorState ? (
                <OverrideEditor
                  mode={editorState.mode}
                  existing={
                    editorState.mode === 'edit' ? editorState.existing : undefined
                  }
                  existingOverrideEvents={existingOverrideEvents}
                  channelOptions={channelLookup.options}
                  channelsConnected={channelLookup.connected}
                  onCancel={() => setEditorState(null)}
                  onSaved={handleEditorSaved}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setEditorState({ mode: 'add' })}
                  className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-3 text-sm text-primary hover:bg-primary/5"
                  data-testid="drawer-add-notification"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add notification override
                </button>
              )}
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

interface OverrideEditorProps {
  mode: 'add' | 'edit'
  existing?: { event: AppSlackEventSubscription; channelId: string }
  existingOverrideEvents: Set<AppSlackEventSubscription>
  channelOptions: Array<{ value: string; label: string }>
  channelsConnected: boolean
  onCancel: () => void
  onSaved: (saved: {
    event: AppSlackEventSubscription
    channelId: string
  }) => void
}

function OverrideEditor({
  mode,
  existing,
  existingOverrideEvents,
  channelOptions,
  channelsConnected,
  onCancel,
  onSaved,
}: OverrideEditorProps) {
  const [event, setEvent] = useState<AppSlackEventSubscription | ''>(
    existing?.event ?? '',
  )
  const [channelId, setChannelId] = useState<string>(existing?.channelId ?? '')

  const eventOptions = useMemo(() => {
    return APP_SLACK_EVENT_SUBSCRIPTIONS.filter((ev) => {
      if (mode === 'edit' && existing?.event === ev) return true
      return !existingOverrideEvents.has(ev)
    }).map((ev) => ({
      value: ev,
      label: `${NOTIFICATION_EVENT_LABELS[ev]} (${ev})`,
    }))
  }, [mode, existing?.event, existingOverrideEvents])

  const canSave = event !== '' && channelId !== '' && channelsConnected

  function handleSave() {
    if (!canSave) return
    onSaved({
      event: event as AppSlackEventSubscription,
      channelId,
    })
  }

  return (
    <div
      className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-3"
      data-testid="drawer-notification-editor"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {mode === 'add' ? 'New override' : 'Edit override'}
      </p>
      {!channelsConnected && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200">
          The workspace's Slack workspace isn't reachable right now — channel
          picker is unavailable. Try again once the connection is restored.
        </p>
      )}
      <div className="space-y-1">
        <Label htmlFor="override-event-picker">Event</Label>
        <SearchableSelect
          id="override-event-picker"
          value={event}
          onChange={(v) => setEvent(v as AppSlackEventSubscription | '')}
          options={eventOptions}
          placeholder={
            eventOptions.length === 0
              ? 'No more events available'
              : 'Pick an event'
          }
          disabled={mode === 'edit' || eventOptions.length === 0}
        />
        {mode === 'edit' && (
          <p className="text-[11px] text-muted-foreground">
            Event is fixed for an existing override — delete and re-add to change it.
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor="override-channel-picker">Slack channel</Label>
        <SearchableSelect
          id="override-channel-picker"
          value={channelId}
          onChange={(v) => setChannelId(v)}
          options={channelOptions}
          placeholder={
            channelOptions.length === 0 ? 'No channels available' : 'Pick a channel'
          }
          disabled={!channelsConnected}
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {mode === 'add' ? 'Add override' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}
